import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/commercial-roofing";
const NAME = "Commercial Roofing";
const TITLE = "Commercial Roofing Phoenix, AZ | Foam Roof Contractor | Durafoam";
const DESCRIPTION =
  "Phoenix commercial foam roofing contractor since 1989. New foam roofs and reroofs, plus repair and recoating on TPO, modified bitumen, and built-up commercial roofs.";

const faqs = [
  {
    q: "How much does a commercial roof cost in Phoenix?",
    a: "Commercial roof pricing in Phoenix varies widely by system and square footage — foam recoats typically run $2.50–$4.50 per sq ft, full SPF installs $6–$9, TPO $7–$11, and modified bitumen $8–$12. See our full breakdown on the commercial roofing cost page for real Phoenix numbers by building type.",
  },
  {
    q: "What commercial roof systems do you install?",
    a: "For new commercial roofs and reroofs in Phoenix, we install spray polyurethane foam (SPF), tile, and shingle. We also service, repair, and recoat existing TPO, modified bitumen, and built-up (BUR) roofs — and when those systems reach end of life, we typically recommend converting to foam for Phoenix commercial buildings.",
  },
  {
    q: "Do you install TPO, modified bitumen, or BUR roofs?",
    a: "No — we don't install new TPO, mod-bit, or BUR systems, but we do inspect, repair, and recoat all three. When one of them is due for replacement, we'll walk you through why foam is almost always the better long-term recommendation for a Phoenix commercial building: seamless, self-flashing around every penetration, re-slopable over ponding areas, and genuinely renewable through recoats instead of tear-off cycles.",
  },
  {
    q: "How long does a commercial foam roof last in Arizona?",
    a: "A properly installed and maintained SPF roof in Phoenix routinely lasts 30+ years. The key is the elastomeric coating on top — recoat every 5 years and the foam substrate underneath stays intact indefinitely. It's the only commercial system in Arizona that's genuinely renewable rather than replaced.",
  },
  {
    q: "TPO vs foam for a Phoenix warehouse — which is better?",
    a: "For a Phoenix warehouse with lots of penetrations (HVAC curbs, drains, conduit), foam wins on almost every axis: it's seamless, self-flashing, adds R-value where warehouses need it most, and can re-slope ponding areas during install. We install foam and routinely convert existing TPO warehouse roofs to foam when they reach end of life.",
  },
  {
    q: "Do you offer commercial roof repair, or only full replacement?",
    a: "Both. A large share of our commercial work is targeted repair — flashings, drain rebuilds, seam repairs, ponding fixes, and coating touch-ups. Repair-first is almost always the right call if the substrate is sound; we'll only recommend replacement when the numbers actually justify it.",
  },
  {
    q: "Can we recoat instead of replacing our commercial roof?",
    a: "Very often, yes — especially for foam and older BUR systems in good structural shape. A fresh elastomeric coating restores reflectivity, seals hairline surface cracks, and can add 10–15 years of service life at 20–30% of replacement cost. It's the single biggest lever on commercial roof economics in Phoenix.",
  },
  {
    q: "Can you re-roof while my business stays open?",
    a: "Almost always. We schedule noisy or odor-producing phases around your operating hours, coordinate roof access with your facilities team, and stage materials to keep loading docks and customer entrances clear. Tenant disruption is one of the first things we plan around, not an afterthought.",
  },
  {
    q: "Do you work with property managers, HOAs, and multifamily portfolios?",
    a: "Yes. We handle recurring maintenance, warranty service, and full-portfolio recoat programs for property management companies, HOA boards, and apartment/condo owners across the Valley. Every visit closes with photos, a written report, and an itemized invoice suitable for reserve accounting.",
  },
  {
    q: "Are you licensed and bonded for commercial work in Arizona?",
    a: "Yes — we hold the appropriate Arizona ROC commercial roofing license, carry commercial general liability and workers' comp, and can provide COIs naming your entity as additional insured before mobilization. Family-owned and operating in Phoenix since 1989.",
  },
  {
    q: "Do you provide budget planning for capital roof replacement?",
    a: "Yes. We produce written condition assessments with estimated remaining service life and replacement cost projections — useful for CapEx planning, reserve studies, and multi-property portfolio budgeting. There's no charge for the assessment on qualifying commercial buildings.",
  },
  {
    q: "What's the best commercial roof for a Phoenix warehouse?",
    a: "For most low-slope warehouse roofs we recommend SPF: highest insulation value per inch, seamless coverage, and self-flashing around the many penetrations a warehouse typically has. It also lets us re-slope ponding areas during the install — something no membrane system can do.",
  },
  {
    q: "Do you handle emergency commercial roof leaks?",
    a: "Yes. Active commercial leaks get same-day or next-day response most weeks — critical for tenants, inventory, and rooftop equipment. See our emergency roof leak repair page for what to do while you wait for a crew to arrive.",
  },
];

export const Route = createFileRoute("/commercial-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="commercial-roofing"
      extraSections={SEARCH_TERM_SECTIONS["commercial-roofing"]}
      eyebrow="Commercial"
      h1="Commercial Roofing Contractor in Phoenix, AZ"
      subhead="Phoenix commercial foam roofing specialists since 1989 — foam is our primary reroof recommendation, with tile and shingle installs for pitched commercial buildings. We also inspect, repair, and recoat existing TPO, modified bitumen, and built-up roofs across warehouses, retail, office, multifamily, HOA, church, and restaurant properties."
      heroBullets={[
        "Foam (SPF) reroofs — our #1 commercial recommendation",
        "Tile & shingle installs for pitched commercial buildings",
        "Repair & recoat on existing TPO, mod-bit, and BUR",
        "Operate-during-work scheduling and property management contracts",
      ]}
      whatIs={{
        heading: "What we install — and what we repair — on Phoenix commercial roofs",
        body: (
          <>
            <p>
              Foam is our hero system. For new commercial roofs and reroofs in Phoenix, foam (SPF)
              is our primary recommendation on nearly every low-slope building, with tile and
              shingle for pitched commercial properties. We also service, repair, and recoat the
              older single-ply and asphalt systems already on Valley rooftops — TPO, modified
              bitumen, and BUR — and give you honest guidance on when to keep repairing vs. convert
              to foam.
            </p>
            <h3 className="mt-8 text-xl font-bold text-primary">What we install (new roofs &amp; reroofs)</h3>
            <ul>
              <li>
                <strong>Spray polyurethane foam (SPF) — our primary commercial system.</strong>{" "}
                Highest R-value per inch, fully seamless, self-flashing around every penetration,
                and the only commercial roof you can genuinely re-slope over ponding without
                tear-off. This is what we recommend for the vast majority of low-slope Phoenix
                commercial buildings. See{" "}
                <Link
                  to="/foam-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  foam roofing
                </Link>{" "}
                and{" "}
                <Link
                  to="/foam-roof-coating-recoating"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  foam recoating
                </Link>
                .
              </li>
              <li>
                <strong>Tile roofing</strong> — for pitched commercial buildings like offices, HOA
                common structures, hospitality, and mixed-use properties where tile matches the
                architecture. See{" "}
                <Link
                  to="/tile-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  tile roofing
                </Link>
                .
              </li>
              <li>
                <strong>Shingle roofing</strong> — for smaller pitched commercial buildings where
                asphalt shingle is the right fit for the geometry and budget. See{" "}
                <Link
                  to="/shingle-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  shingle roofing
                </Link>
                .
              </li>
            </ul>
            <h3 className="mt-8 text-xl font-bold text-primary">
              What we inspect, repair, and recoat (but don&apos;t install new)
            </h3>
            <p>
              Plenty of Phoenix commercial buildings already have TPO, mod-bit, or BUR on them.
              We&apos;ll keep those roofs alive for as long as it makes financial sense — and when
              it doesn&apos;t, we&apos;ll walk you through why foam is almost always the right
              reroof for a low-slope Phoenix building.
            </p>
            <ul>
              <li>
                <strong>TPO (thermoplastic polyolefin):</strong> We inspect seams, repair
                punctures, rebuild flashings around HVAC curbs and drains, and address failed
                terminations. When the membrane reaches end of life we typically recommend
                converting to foam — a foam overlay is seamless, self-flashing at every
                penetration, adds real R-value, and lets us re-slope ponding areas the original
                TPO install couldn&apos;t.
              </li>
              <li>
                <strong>Modified bitumen (mod-bit):</strong> We repair blisters, seam separations,
                and flashing failures, and coat the field to restore reflectivity. Once the
                asphalt reaches end of life, a foam-over reroof usually pencils out better than a
                second mod-bit install — no more torch work, no more repeat replacement cycles.
              </li>
              <li>
                <strong>Built-up roofing (BUR / tar-and-gravel):</strong> We handle targeted
                repairs, drain rebuilds, and coating maintenance while the built-up is still
                serviceable. When it isn&apos;t, foam is the reroof: lighter, better insulated,
                and renewable through recoats instead of another gravel tear-off.
              </li>
            </ul>
            <p>
              Bottom line: foam is what we install and what we recommend for a Phoenix commercial
              reroof. TPO, mod-bit, and BUR are systems we&apos;ll keep serviced honestly — and
              transition you off of when it&apos;s time.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Commercial roof considerations for Phoenix",
        body: (
          <>
            <p>
              Commercial buildings here deal with the same 115° heat and monsoon storms residential
              roofs face — but with additional variables: rooftop HVAC, heavier foot traffic, more
              penetrations, tenant uptime requirements, and reserve-study budget cycles.
            </p>
            <ul>
              <li>
                <strong>Rooftop HVAC:</strong> Phoenix commercial buildings live or die on AC.
                A reflective white foam roof with fresh elastomeric top coat cuts mechanical load
                and extends HVAC compressor life measurably — the same benefit an aging TPO or
                mod-bit gets back the day we recoat it.
              </li>
              <li>
                <strong>Penetration density:</strong> Vents, drains, conduit, equipment curbs, and
                gas lines are all potential leak paths. SPF wraps them continuously; single-ply
                systems require meticulous flashing on every one.
              </li>
              <li>
                <strong>Ponding water:</strong> Common on Phoenix flat roofs as buildings settle.
                Foam is the only system that can be shot thicker in low spots to actively re-slope
                a roof toward existing drains — no tear-off required.
              </li>
              <li>
                <strong>Tenant disruption:</strong> Most commercial reroofs happen while tenants
                operate. We plan crew hours, roof access, and material staging around your
                operations, not the other way around.
              </li>
              <li>
                <strong>Cool-roof rebates:</strong> Several Phoenix-metro utility programs offer
                rebates for reflective "cool roof" installations meeting SRI thresholds. We help
                document the specs at proposal time.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our commercial roofing process",
        steps: [
          {
            title: "Site assessment & condition report",
            body: "A senior estimator walks the roof, measures, and photo-documents conditions — including drains, penetrations, flashings, and any ponding. We meet with your facilities or property management team to understand operational constraints before we quote.",
          },
          {
            title: "System recommendation & written proposal",
            body: "Written proposal with system specification, manufacturer, warranty terms, staging plan, and schedule. When more than one system honestly fits the building, we quote multiple options side by side so you can make a real decision.",
          },
          {
            title: "Coordinated install with your operations",
            body: "Crews coordinate directly with your facilities team to minimize disruption. Daily progress updates, weather contingencies, controlled staging, and clean-as-we-go site management. COIs and safety paperwork provided before mobilization.",
          },
          {
            title: "Warranty, maintenance & long-term service plan",
            body: "Manufacturer warranty plus our workmanship warranty. Optional ongoing maintenance program protects the warranty across its full life and typically extends real service life by 5–10 years beyond spec.",
          },
        ],
      }}
      whyChoose={{
        heading: "Commercial roof repair vs. recoat vs. replacement",
        body: (
          <>
            <p>
              Most commercial roof calls in Phoenix don't need full replacement — they need honest
              triage. We'll only recommend a full tear-off when the substrate is genuinely gone.
              Here's how we think about it:
            </p>
            <ul>
              <li>
                <strong>Repair</strong> — Isolated leaks, drain rebuilds, seam failures, flashing
                separations, storm damage. Substrate is sound and the rest of the roof has real
                service life left. See{" "}
                <Link
                  to="/flat-roof-repair"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  flat roof repair
                </Link>{" "}
                and{" "}
                <Link
                  to="/roof-leak-repair"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  roof leak repair
                </Link>{" "}
                for how we approach it.
              </li>
              <li>
                <strong>Recoat</strong> — Coating has weathered off, reflectivity is gone, and
                minor surface cracking is showing, but the foam or bitumen underneath is still
                intact. A fresh elastomeric coating restores waterproofing and reflectivity at
                20–30% of replacement cost and adds 10–15 years. Details on{" "}
                <Link
                  to="/foam-roof-coating-recoating"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  foam roof recoating
                </Link>
                .
              </li>
              <li>
                <strong>Replacement</strong> — Substrate is saturated, structurally compromised, or
                so patched it's no longer serviceable. Or you're changing systems for a reason
                (cool-roof compliance, insurance, code). This is when tear-off and a new build-up
                pencils out.
              </li>
            </ul>
            <p>
              For a written recommendation on your specific building,{" "}
              <Link
                to="/free-roof-inspection-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                request a free commercial roof inspection
              </Link>{" "}
              or see our{" "}
              <Link
                to="/commercial-roofing-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                commercial roofing cost guide
              </Link>{" "}
              for real Phoenix price ranges. Cost the blocker? <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Ask us about payment options</Link>.
            </p>
          </>
        ),
      }}
      extraSection={{
        heading: "Commercial roofing by building type",
        body: (
          <>
            <p>
              Different commercial building types have different roof problems. What matters on a
              distribution warehouse is different from what matters on a strip retail center or a
              multifamily community.
            </p>
            <ul>
              <li>
                <strong>Warehouse & distribution:</strong> Large low-slope roofs with dense HVAC,
                skylights, and drain networks. Foam is the recommendation — see{" "}
                <Link
                  to="/warehouse-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  warehouse roofing
                </Link>{" "}
                for our full warehouse process, including foam-over conversions on aging TPO and
                mod-bit. We run warehouse work along the{" "}
                <Link
                  to="/tempe-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Tempe/I-10 industrial corridor
                </Link>{" "}
                and the West Valley regularly.
              </li>
              <li>
                <strong>Retail & strip centers:</strong> Multiple tenants, phased scheduling, and
                storefront-visible staging. We plan around business hours and never block customer
                access. Common in{" "}
                <Link
                  to="/scottsdale-roofing-contractor"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Scottsdale
                </Link>
                ,{" "}
                <Link
                  to="/mesa-roofing-contractor"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Mesa
                </Link>
                , and{" "}
                <Link
                  to="/chandler-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Chandler
                </Link>{" "}
                shopping corridors.
              </li>
              <li>
                <strong>Office buildings:</strong> Class-A appearance requirements, tenant AC
                dependency, and executive access to the roof. Reflective foam with careful edge
                and parapet detailing is our reroof recommendation; on pitched office buildings we
                also install tile. Existing TPO gets repaired and maintained until a foam reroof
                pencils out.
              </li>
              <li>
                <strong>Multifamily, apartments & condos:</strong> Multiple owners or tenants,
                night/early-morning noise sensitivities, and coordinated unit notification. See{" "}
                <Link
                  to="/apartment-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  apartment & condo roofing
                </Link>{" "}
                for our full multifamily process.
              </li>
              <li>
                <strong>HOA common areas & community buildings:</strong> Board decision cycles,
                reserve studies, and community-wide phased recoats. See{" "}
                <Link
                  to="/hoa-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  HOA roofing
                </Link>{" "}
                for how we work with boards and management companies.
              </li>
              <li>
                <strong>Churches & nonprofits:</strong> Weekend-service scheduling and mission-fit
                budget conversations. Foam recoats are usually the highest-ROI recommendation on
                aging church roofs across the Valley.
              </li>
              <li>
                <strong>Restaurants:</strong> Grease-exhaust penetrations, hood curbs, and roof
                traffic from kitchen equipment service. Detail work at penetrations is where
                restaurant roofs live or die — that's where we spend our time.
              </li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Foam reroof, tile or shingle install, or a TPO/mod-bit/BUR that needs a plan?"
      ctaBody="Call us or request a free commercial inspection. We'll walk the roof, tell you honestly whether to repair, recoat, or convert to foam, and hand you a written condition report you can share with your board, CFO, or property management team."
    />
  );
}
